/**
 * `ItemSubscribersSource` over the C4 api. Structurally typed so this module never imports
 * server code. The **write** is `items.subscribe` / `items.unsubscribe`, which existed before
 * this round — C4 deliberately added no second writer of `item_subscribers`.
 */
import type { ItemSubscribersSource, SubscriberView } from './ItemSubscribers';

interface Query<TIn, TOut> {
  query(input: TIn): Promise<TOut>;
}
interface Mutate<TIn, TOut> {
  mutate(input: TIn): Promise<TOut>;
}

export interface SubscribersTrpc {
  items: {
    subscribers: Query<{ itemId: string }, SubscriberView[]>;
    isSubscribed: Query<{ itemId: string }, boolean>;
    subscribe: Mutate<{ itemId: string; userIds: string[] }, unknown>;
    unsubscribe: Mutate<{ itemId: string; userIds: string[] }, unknown>;
  };
}

export function createSubscribersSource(trpc: SubscribersTrpc): ItemSubscribersSource {
  return {
    list: (itemId) => trpc.items.subscribers.query({ itemId }),
    isSubscribed: (itemId) => trpc.items.isSubscribed.query({ itemId }),
    subscribe: (itemId, userIds) => trpc.items.subscribe.mutate({ itemId, userIds }),
    unsubscribe: (itemId, userIds) => trpc.items.unsubscribe.mutate({ itemId, userIds }),
  };
}

/** A fixture source for `/__ui/collab` and the unit tests. Invented people only. */
export function createFixtureSubscribersSource(initial: SubscriberView[] = []): ItemSubscribersSource {
  let rows = [...initial];
  return {
    async list() {
      return rows;
    },
    async isSubscribed(_itemId) {
      return rows.some((row) => row.id === '5000000000001');
    },
    async subscribe(_itemId, userIds) {
      for (const id of userIds) {
        if (!rows.some((row) => row.id === id)) {
          rows.push({ id, kind: 'user', name: `User ${id.slice(-2)}`, email: null, photoUrl: null, title: null });
        }
      }
    },
    async unsubscribe(_itemId, userIds) {
      rows = rows.filter((row) => !userIds.includes(row.id));
    },
  };
}
